import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { mockData } from '../mock';
import { Award, Truck, Headphones, Tag } from 'lucide-react';

const iconMap = {
  award: Award,
  truck: Truck,
  headphones: Headphones,
  tag: Tag,
};

export const Manifesto = () => {
  const containerRef = useRef(null);

  return (
    <section id="about" ref={containerRef} className="relative py-32 px-6 bg-[#0a1628] overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-[#7dd3fc]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#7dd3fc]/5 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Sobre <span className="text-[#7dd3fc]">Nós</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Nossos diferenciais que fazem toda a diferença
          </p>
        </motion.div>

        {/* Manifesto Chapters */}
        <div className="space-y-24">
          {mockData.manifesto.map((item, index) => (
            <motion.div
              key={item.number}
              initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className={`flex flex-col md:flex-row items-center gap-6 md:gap-12 ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Number */}
              <motion.div
                className="relative flex-shrink-0"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="text-[8rem] sm:text-[12rem] md:text-[16rem] font-bold text-[#7dd3fc]/10 leading-none">
                  {item.number}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-5xl sm:text-7xl md:text-8xl font-bold text-[#7dd3fc]">
                    {item.number}
                  </div>
                </div>
              </motion.div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6">
                  {item.title}
                </h3>
                <p className="text-base sm:text-lg md:text-xl text-white/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Differentials Grid */}
        <div className="mt-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {mockData.differentials.map((diff, index) => {
            const Icon = iconMap[diff.icon] || Tag;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="group relative p-8 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl border border-white/10 hover:border-[#7dd3fc]/50 transition-all duration-500"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#7dd3fc]/0 to-[#7dd3fc]/0 group-hover:from-[#7dd3fc]/10 group-hover:to-[#7dd3fc]/5 rounded-2xl transition-all duration-500" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#7dd3fc]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#7dd3fc]/20 transition-colors duration-300">
                    <Icon className="text-[#7dd3fc]" size={32} />
                  </div>
                  
                  <h4 className="text-xl font-bold text-white mb-3">
                    {diff.title}
                  </h4>
                  
                  <p className="text-white/60 leading-relaxed">
                    {diff.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
